// Lengths the way the shop writes them. Inputs accept `24 3/8`, `24-3/8`, `3/8`, `24.375`
// and `2' 6 1/2"`; they are stored as decimal inches. Showing them back as fractions is a
// display concern, and lives here so every screen does it the same way.

import { Rat } from './rational.ts';

const TWELVE = Rat.of(12n);

function parseInches(text: string): Rat | null {
  const s = text.trim().replace(/"|”|″|\bin\.?$/gi, '').trim();
  if (s === '') return null;
  // whole and fraction: "24 3/8" or "24-3/8"
  let m = /^(-)?(\d+)[\s-]+(\d+)\s*\/\s*(\d+)$/.exec(s);
  if (m) {
    if (m[4] === '0' || /^0+$/.test(m[4]!)) return null;
    const v = Rat.of(BigInt(m[2]!)).add(Rat.of(BigInt(m[3]!), BigInt(m[4]!)));
    return m[1] ? v.neg() : v;
  }
  // bare fraction: "3/8"
  m = /^(-)?(\d+)\s*\/\s*(\d+)$/.exec(s);
  if (m) {
    if (/^0+$/.test(m[3]!)) return null;
    const v = Rat.of(BigInt(m[2]!), BigInt(m[3]!));
    return m[1] ? v.neg() : v;
  }
  return Rat.tryParse(s);
}

/** Parse a length to exact decimal inches. Returns null when the text is not a length. */
export function parseLength(text: string | number): Rat | null {
  if (typeof text === 'number') return Rat.tryParse(text);
  const s = text.trim();
  if (s === '') return null;
  // feet and inches: 2' 6 1/2"  or  2'
  const ft = /^(-)?(\d+(?:\.\d+)?)\s*(?:'|’|′|ft\.?)\s*(.*)$/i.exec(s);
  if (ft) {
    const feet = Rat.tryParse(ft[2]!);
    if (!feet) return null;
    const rest = ft[3]!.trim();
    const inches = rest === '' ? Rat.ZERO : parseInches(rest);
    if (!inches) return null;
    const v = feet.mul(TWELVE).add(inches);
    return ft[1] ? v.neg() : v;
  }
  return parseInches(s);
}

/**
 * Show decimal inches as the shop reads them: `24 3/8`. Exact to the given denominator
 * (64ths by default); a value that is not a clean fraction is shown as a decimal instead
 * of being rounded silently.
 */
export function formatLength(value: Rat | string | number, maxDenominator = 64): string {
  const v = value instanceof Rat ? value : Rat.tryParse(value);
  if (!v) return String(value);
  const neg = v.sign() < 0;
  const a = v.abs();
  const scaled = a.mul(Rat.of(BigInt(maxDenominator)));
  if (scaled.d !== 1n) return v.toDecimal(4);
  const whole = a.floor();
  const frac = a.sub(Rat.of(whole));
  const sign = neg ? '-' : '';
  if (frac.isZero()) return `${sign}${whole}`;
  return whole === 0n ? `${sign}${frac.n}/${frac.d}` : `${sign}${whole} ${frac.n}/${frac.d}`;
}
